import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {
    Alert, Box, Button, Chip, Skeleton, Stack, Tab, Tabs, Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {AltRoute} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle";
import BetaBadge from "./layout/BetaBadge";
import Constants from "../../utils/constants";
import {useGetSimulationQuery} from "../../modules/simulations/simulations.api";
import TableJournees from "./simulations/TableJournees";
import BlocJournee from "./simulations/BlocJournee";
import VerdictSimulation from "./simulations/VerdictSimulation";
import BoutonExporter from "./simulations/BoutonExporter";
import PiluleDistances from "./simulations/PiluleDistances";
import RecapCampagne from "./simulations/RecapCampagne";
import PiluleErreur from "./simulations/PiluleErreur";
import BlocSensibilite from "./simulations/BlocSensibilite";
import {cleCranUrl} from "./simulations/sensibiliteRules";
import BadgeImporte from "./simulations/BadgeImporte";
import {texteSnapshot} from "./simulations/echangeScenarioRules";
import {lignesNonAffectees, paramsUrl, verdictSimulation} from "./simulations/atelierRules";
import {
    libelleEquipage, paramFiger,
    pollingSimulation, raisonsGenerales, resumeContraint,
} from "./simulations/simulationRules";

function SimulationPage() {
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const clientId = searchParams.get('client');
    const navigate = useNavigate();
    const [polling, setPolling] = useState(0);
    const {data: simulation, isLoading, error} = useGetSimulationQuery(id, {pollingInterval: polling});
    const [date, setDate] = useState(null);
    const [vue, setVue] = useState(searchParams.get('vue') === 'recap' ? 'recap' : 'journees');

    useEffect(() => {
        setPolling(pollingSimulation(simulation));
    }, [simulation]);

    if (isLoading) {
        return (
            <>
                <PageTitle title="Scénario" icon={<AltRoute/>}/>
                <Skeleton variant="rectangular" height={120}/>
                <Skeleton variant="rectangular" height={300} sx={{mt: 2}}/>
            </>
        );
    }
    if (error || !simulation) {
        return (
            <>
                <PageTitle title="Scénario" icon={<AltRoute/>}/>
                <Alert severity="error">{error?.status === 404 ? "Scénario introuvable." : "Impossible de charger le scénario."}</Alert>
                <Button color="primary" onClick={() => navigate(Constants.APP_ROUTE_SIMULATIONS)}>Retour aux simulations</Button>
            </>
        );
    }

    const p = simulation.parametres || {};
    const t = simulation.totaux;
    const retour = Constants.APP_ROUTE_SIMULATIONS + paramsUrl({client: clientId, patrimoine: simulation.patrimoine?.id});
    const atelier = (extra = {}) => Constants.APP_ROUTE_SIMULATIONS_ATELIER + paramsUrl({client: clientId, patrimoine: simulation.patrimoine?.id, from: simulation.id, ...extra});
    const nonAffectees = simulation.nonAffectees?.liste ?? [];

    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center">
                    <PageTitle title={simulation.libelle} icon={<AltRoute/>}/>
                    <BetaBadge/>
                    <BadgeImporte simulation={simulation} sx={{ml: 1}}/>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <BoutonExporter simulation={simulation}/>
                    <Button color="secondary" variant="contained" sx={{color: 'white', borderRadius: 999}} onClick={() => navigate(atelier())}>
                        Ouvrir dans l'atelier
                    </Button>
                    <Button color="primary" onClick={() => navigate(retour)}>Retour aux scénarios</Button>
                </Stack>
            </Stack>

            <Box sx={{mb: 2}}><VerdictSimulation verdict={verdictSimulation(simulation)}
                sousLigne={`${simulation.enregistre ? 'Enregistré' : 'Brouillon'}${simulation.statut === 'done' && t?.horizon != null ? ` · horizon ${t.horizon} journées` : ''}${texteSnapshot(simulation) ? ` · ${texteSnapshot(simulation)}` : ''}`}
                details={simulation.statut === 'done' ? lignesNonAffectees(simulation) : []}/>
                {simulation.statut === 'done' && <PiluleDistances distances={t?.distances} sx={{mt: 1}}/>}
                {simulation.statut === 'done' && <PiluleErreur erreurMesuree={simulation.erreurMesuree} patrimoineId={simulation.patrimoine?.id} sx={{mt: 1}}/>}
                {simulation.statut === 'done' && <BlocSensibilite simulation={simulation} sx={{mt: 1}}
                    onAppliquer={(ligne) => navigate(atelier({cran: cleCranUrl(ligne.cle)}))}/>}</Box>

            <div className="sectionContainer">
                <Typography variant="body2">
                    {simulation.patrimoine?.libelle} · du {simulation.du} au {simulation.au}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mt: 1}}>
                    <Chip size="small" label={`${p.nbEquipes} équipe(s)`}/>
                    <Chip size="small" label={`équipage ${libelleEquipage(p.equipage)}`}/>
                    <Chip size="small" label={resumeContraint(p)}/>
                    <Chip size="small" label={p.deplacement === false ? 'aller-retour base' : 'déplacement'}/>
                    {p.curseurs && <Chip size="small" label={`${p.curseurs.heuresParJour} h × ${p.curseurs.joursParSemaine} j`}/>}
                    {p.curseurs?.coutKm != null && <Chip size="small" label={`${p.curseurs.coutKm} €/km`}/>}
                    {(p.verrous?.jours?.length > 0) && <Chip size="small" label={`${p.verrous.jours.length} PAV figé(s)`}/>}
                    {(p.verrous?.interdits?.length > 0) && <Chip size="small" label={`${p.verrous.interdits.length} date(s) interdite(s)`}/>}
                </Stack>

                {simulation.statut === 'done' && t &&
                    <>
                        {raisonsGenerales(simulation.raisons).map((r, i) => <Typography key={i} variant="caption" sx={{display: 'block'}}>{r}</Typography>)}
                    </>}
            </div>

            {simulation.statut === 'done' &&
                <>
                    <Tabs value={vue} onChange={(_e, v) => setVue(v)} sx={{mt: 3}} aria-label="vue du scénario">
                        <Tab value="journees" label="Journées"/>
                        <Tab value="recap" label="Récap"/>
                    </Tabs>
                    {vue === 'recap' && <RecapCampagne simulationId={simulation.id} onRejouer={() => navigate(atelier())}/>}
                    {vue === 'journees' && <>
                    <Typography variant="h5" className="sectionHeader" sx={{mt: 3}}>Journées</Typography>
                    <TableJournees simulation={simulation} estSelectionnee={(j) => date === j.date}
                                   onSelect={(j) => setDate(date === j.date ? null : j.date)}/>

                    {date && <BlocJournee simulation={simulation} date={date} onFiger={(nom, d) => navigate(atelier({figer: paramFiger(nom, d)}))}/>}

                    {nonAffectees.length > 0 &&
                        <div className="sectionContainer">
                            <Typography variant="h5" className="sectionHeader">Visites non affectées ({simulation.nonAffectees.nombre})</Typography>
                            {nonAffectees.map((n, i) => (
                                <Typography key={i} variant="body2">
                                    <strong>{n.nomenclature}</strong> ({[].concat(n.flux ?? []).join(', ')}) — {n.raison}
                                </Typography>
                            ))}
                        </div>}
                    </>}
                </>}

            <Typography variant="caption" sx={{display: 'block', mt: 2}}>Simulation : ne crée rien dans l'ERP.</Typography>
        </>
    );
}

export default SimulationPage;
